import {
  LogLevel,
  PublicClientApplication,
  type Configuration,
} from '@azure/msal-browser';
import {
  API_SCOPES,
  INVENTORY_API_SCOPES,
  INVENTORY_V3_API_SCOPES,
  MSAL_CLIENT_ID,
  MSAL_POST_LOGOUT_REDIRECT_URI,
  MSAL_REDIRECT_URI,
  MSAL_SCOPES,
  MSAL_TENANT_ID,
  ENVIRONMENT,
  HUB_SCOPES,
  AI_ANALYZER_SCOPES,
  SITE_COMPLIANCE_API_SCOPES,
} from './env';

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

const isDevelopment = ENVIRONMENT === 'development';

export const msalConfig: Configuration = {
  auth: {
    clientId: MSAL_CLIENT_ID, // This is the ONLY mandatory field that you need to supply.
    authority: `https://login.microsoftonline.com/${MSAL_TENANT_ID}`, // Replace the placeholder with your tenant info
    redirectUri: MSAL_REDIRECT_URI, // Points to window.location.origin. You must register this URI on Microsoft Entra admin center/App Registration.
    postLogoutRedirectUri: MSAL_POST_LOGOUT_REDIRECT_URI, // Indicates the page to navigate after logout.
    navigateToLoginRequestUrl: true, // If "true", will navigate back to the original request location before processing the auth code response.
  },
  cache: {
    cacheLocation: 'localStorage', // Configures cache location. "sessionStorage" is more secure, but "localStorage" gives you SSO between tabs.
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      loggerCallback: (level, message, containsPii) => {
        if (containsPii || !isDevelopment) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

export const msalInstance = new PublicClientApplication(msalConfig);

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  scopes: MSAL_SCOPES,
};

export const inventoryRequest = {
  scopes: INVENTORY_API_SCOPES,
};

export const inventoryV3Request = {
  scopes: INVENTORY_V3_API_SCOPES,
};

export const conformanceRequest = {
  scopes: API_SCOPES,
};

export const hubRequest = {
  scopes: HUB_SCOPES,
}

export const aiAnalyzerRequest = {
  scopes: AI_ANALYZER_SCOPES,
}

export const siteComplianceRequest = {
  scopes: SITE_COMPLIANCE_API_SCOPES,
};

// Add here the endpoints for MS Graph API services you would like to use.
export const graphConfig = {
  graphMeEndpoint: 'https://graph.microsoft.com/v1.0/me',
  graphUserPhotoEndpoint: 'https://graph.microsoft.com/v1.0/me/photo/$value',
};

/**
 * An optional silentRequest object can be used to achieve silent SSO
 * between applications by providing a "login_hint" property.
 */
// export const silentRequest = {
//     scopes: ["openid", "profile"],
//     loginHint: "example@domain.net"
// };
